import { beforeEach, describe, expect, it, vi } from 'vitest';

import middleware from './guest-session';

// vi.hoisted so the Nuxt globals exist before the middleware module is imported: its top level calls
// defineNuxtRouteMiddleware immediately, and the mock factories below run at import time too, so
// plain `const` declarations would still be in their temporal dead zone.
const { clearGuestSession, exchangeClaimLink, getGuestToken, navigateTo, setGuestReturnPath } = vi.hoisted(() => {
  const fns = {
    clearGuestSession: vi.fn(),
    exchangeClaimLink: vi.fn(),
    getGuestToken: vi.fn(),
    navigateTo: vi.fn(),
    setGuestReturnPath: vi.fn(),
  };

  vi.stubGlobal('defineNuxtRouteMiddleware', handler => handler);
  vi.stubGlobal('navigateTo', fns.navigateTo);
  vi.stubGlobal('useRuntimeConfig', () => ({ public: { apiBaseUrl: '/api' } }));

  return fns;
});

vi.mock('@pulse/utils/guest-session', () => ({ clearGuestSession, getGuestToken }));
vi.mock('~/support/guest-return-path', () => ({ setGuestReturnPath }));
// A function constructor rather than a class field: the eslint parser for .js in this app does not
// accept class properties.
vi.mock('@pulse/services/ClaimService', () => ({
  default: function ClaimServiceMock() {
    this.exchangeClaimLink = exchangeClaimLink;
  },
}));

const route = (query = {}, path = '/view/claim-1') => ({
  fullPath: path,
  path,
  query,
});

describe('guest-session middleware', () => {
  beforeEach(() => {
    [getGuestToken, clearGuestSession, setGuestReturnPath, exchangeClaimLink, navigateTo].forEach(m => m.mockReset());
    navigateTo.mockImplementation(path => ({ redirectedTo: path }));
  });

  it('lets a request with a session through untouched', async () => {
    getGuestToken.mockReturnValue('token');

    expect(await middleware(route())).toBeUndefined();
    expect(navigateTo).not.toHaveBeenCalled();
  });

  it('sends a request with no session to lookup and records where to resume', async () => {
    getGuestToken.mockReturnValue(null);

    await middleware(route());

    expect(setGuestReturnPath).toHaveBeenCalledWith('/view/claim-1');
    expect(navigateTo).toHaveBeenCalledWith('/start');
  });

  // The bug this guards: holding a session for order A, opening an expired link for order B used to
  // fall back to A's session, load the page, and fail its first request with a 403.
  it('does not fall back to an existing session when the link fails to redeem', async () => {
    getGuestToken.mockReturnValue(null); // cleared, so nothing in hand after redemption fails
    exchangeClaimLink.mockRejectedValue(new Error('expired'));

    await middleware(route({ t: 'expired-link' }));

    expect(clearGuestSession).toHaveBeenCalled();
    expect(navigateTo).toHaveBeenCalledWith('/start');
  });

  it('clears the old session before attempting redemption, not after', async () => {
    const order = [];
    clearGuestSession.mockImplementation(() => order.push('clear'));
    exchangeClaimLink.mockImplementation(() => {
      order.push('exchange');

      return Promise.resolve();
    });
    getGuestToken.mockReturnValue('fresh-token');

    await middleware(route({ t: 'good-link' }));

    expect(order).toEqual(['clear', 'exchange']);
  });

  it('strips the spent link token from the url after a successful redemption', async () => {
    exchangeClaimLink.mockResolvedValue(undefined);
    getGuestToken.mockReturnValue('fresh-token');

    await middleware(route({ t: 'good-link' }));

    expect(navigateTo).toHaveBeenCalledWith('/view/claim-1', { replace: true });
  });

  it('keeps other query params when stripping the link token', async () => {
    exchangeClaimLink.mockResolvedValue(undefined);
    getGuestToken.mockReturnValue('fresh-token');

    await middleware(route({ rating: '5', t: 'good-link' }));

    expect(navigateTo).toHaveBeenCalledWith('/view/claim-1?rating=5', { replace: true });
  });

  it('does not carry the spent token into the resume path', async () => {
    exchangeClaimLink.mockRejectedValue(new Error('expired'));
    getGuestToken.mockReturnValue(null);

    await middleware(route({ t: 'expired-link' }));

    expect(setGuestReturnPath).toHaveBeenCalledWith('/view/claim-1');
  });

  // Older store template overrides append their own `?rating=N` to a link that already carries the
  // token, so the browser folds it into the token's value and the signature stops verifying. Fixing
  // it here rescues links that are already in customers' inboxes.
  describe('a link corrupted by a second question mark', () => {
    const JWT = 'eyJhbGciOiJIUzI1NiJ9.eyJjbGFpbUlkIjoiY2xhaW0tMSJ9.sig';

    it('redeems the token without the junk appended to it', async () => {
      exchangeClaimLink.mockResolvedValue(undefined);
      getGuestToken.mockReturnValue('fresh-token');

      await middleware(route({ t: `${JWT}?rating=5` }, '/view/claim-1/review'));

      expect(exchangeClaimLink).toHaveBeenCalledWith(JWT);
    });

    it('puts the rescued rating back so the review page preselects the star', async () => {
      exchangeClaimLink.mockResolvedValue(undefined);
      getGuestToken.mockReturnValue('fresh-token');

      await middleware(route({ t: `${JWT}?rating=5` }, '/view/claim-1/review'));

      expect(navigateTo).toHaveBeenCalledWith('/view/claim-1/review?rating=5', { replace: true });
    });

    it('leaves an uncorrupted token exactly as it was', async () => {
      exchangeClaimLink.mockResolvedValue(undefined);
      getGuestToken.mockReturnValue('fresh-token');

      await middleware(route({ t: JWT }, '/view/claim-1/review'));

      expect(exchangeClaimLink).toHaveBeenCalledWith(JWT);
    });
  });
});
